/**
 * Shared Device Outbox
 *
 * IndexedDB storage for offline queued mutations on a shared device:
 * - Database per device: `helper-outbox-shared-${deviceId}`
 * - Object store `items`, keyPath `opId`
 * - Validates that offlineSessionId belongs to personUid before enqueuing
 * - Flushes items in batches of SHARED_DEVICE_MAX_QUEUED_ITEMS (50) via submitSharedDeviceQueued
 * - Marks ok items deleted, refused items REFUSED with code
 * - Reverts SYNCING items to PENDING on network error
 * - Halts without modifying entries on revoked device error
 * - Mutual exclusion: only one flush runs at a time per device
 */

import {
  SHARED_DEVICE_MAX_QUEUED_ITEMS,
  type SharedDeviceQueuedItemKind,
  type SubmitSharedDeviceQueuedResult,
} from '../types/ops'
import type { SharedDeviceQueuedItem } from '../../functions-common/src/ops/sharedDevices'
import {
  finalizeSharedDeviceUpload,
  issueSharedDeviceUploadUrl,
  submitSharedDeviceQueued,
} from './opsSharedDevicesService'
import { getOfflineSession } from './sharedDeviceOfflineStore'
import { mapSharedDeviceError } from './sharedDeviceErrorMapper'

const DB_VERSION = 2
const STORE_ITEMS = 'items'
const STORE_BLOBS = 'blobs'

export type SharedDeviceQueuedKind = SharedDeviceQueuedItemKind
export type SharedDeviceOutboxStatus = 'PENDING' | 'SYNCING' | 'REFUSED'

export interface SharedDeviceOutboxBlob {
  blobKey: string
  blob: Blob
  contentType: string
  size: number
  createdAt: number
}

export interface SharedDeviceOutboxEntry {
  opId: string
  personUid: string
  offlineSessionId: string
  at: number
  kind: SharedDeviceQueuedKind
  payload: Record<string, unknown>
  status: SharedDeviceOutboxStatus
  code?: string
  createdAt: number
  blobKey?: string
  blobKeys?: string[]
  fileId?: string
  fileIds?: string[]
  photoBindings?: Array<{
    fieldKey: string
    blobKey: string
    fileName?: string
    contentType?: string
    fileId?: string
  }>
  recordId?: string
}

// Map of open database promises per device
const dbCache = new Map<string, Promise<IDBDatabase>>()

// Active flush mutex per device
const activeFlushes = new Set<string>()

const openOutboxDb = (deviceId: string): Promise<IDBDatabase> => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'))
  }
  const cached = dbCache.get(deviceId)
  if (cached) return cached

  const dbName = `helper-outbox-shared-${deviceId}`
  const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_ITEMS)) {
        const store = db.createObjectStore(STORE_ITEMS, { keyPath: 'opId' })
        store.createIndex('status', 'status', { unique: false })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'blobKey' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IDB open failed'))
  })

  dbCache.set(deviceId, dbPromise)
  return dbPromise
}

const runOutboxTx = async <T>(
  deviceId: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
  storeName: string = STORE_ITEMS,
): Promise<T> => {
  const db = await openOutboxDb(deviceId)
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    const store = tx.objectStore(storeName)

    let resultPromise: Promise<T> | IDBRequest<T>
    try {
      resultPromise = work(store)
    } catch (err) {
      reject(err)
      return
    }

    if (resultPromise instanceof Promise) {
      resultPromise.then(
        (res) => {
          tx.oncomplete = () => resolve(res)
        },
        (err) => reject(err),
      )
    } else {
      resultPromise.onsuccess = () => resolve(resultPromise.result)
      resultPromise.onerror = () => reject(resultPromise.error ?? new Error('Transaction request failed'))
    }

    tx.onerror = () => reject(tx.error ?? new Error('Transaction failed'))
  })
}

export async function storeSharedDeviceBlob(
  deviceId: string,
  entry: SharedDeviceOutboxBlob,
): Promise<void> {
  await runOutboxTx(
    deviceId,
    'readwrite',
    (store) => store.put(entry as unknown as Record<string, unknown>),
    STORE_BLOBS,
  )
}

export async function getSharedDeviceBlob(
  deviceId: string,
  blobKey: string,
): Promise<SharedDeviceOutboxBlob | null> {
  const res = await runOutboxTx(
    deviceId,
    'readonly',
    (store) => store.get(blobKey),
    STORE_BLOBS,
  )
  return (res as unknown as SharedDeviceOutboxBlob) ?? null
}

export async function deleteSharedDeviceBlob(
  deviceId: string,
  blobKey: string,
): Promise<void> {
  await runOutboxTx(
    deviceId,
    'readwrite',
    (store) => store.delete(blobKey),
    STORE_BLOBS,
  )
}



/**
 * Enqueues a new item into the shared-device outbox.
 * Refuses if offlineSessionId is not a stored offline session for personUid.
 */
export async function enqueueSharedDeviceItem(
  deviceId: string,
  item: {
    opId: string
    personUid: string
    offlineSessionId: string
    at: number
    kind: SharedDeviceQueuedKind
    payload: Record<string, unknown>
    createdAt?: number
    blobKey?: string
    blobKeys?: string[]
    fileId?: string
    fileIds?: string[]
    photoBindings?: Array<{
      fieldKey: string
      blobKey: string
      fileName?: string
      contentType?: string
      fileId?: string
    }>
    recordId?: string
  },
): Promise<void> {
  const session = await getOfflineSession(deviceId, item.offlineSessionId)
  if (!session || session.uid !== item.personUid) {
    throw new Error('Refused: offlineSessionId is not a valid offline session for this person')
  }

  const entry: SharedDeviceOutboxEntry = {
    opId: item.opId,
    personUid: item.personUid,
    offlineSessionId: item.offlineSessionId,
    at: item.at,
    kind: item.kind,
    payload: item.payload,
    status: 'PENDING',
    createdAt: item.createdAt ?? Date.now(),
    blobKey: item.blobKey,
    blobKeys: item.blobKeys,
    fileId: item.fileId,
    fileIds: item.fileIds,
    photoBindings: item.photoBindings,
    recordId: item.recordId,
  }

  await runOutboxTx(deviceId, 'readwrite', (store) => {
    return store.put(entry as unknown as Record<string, unknown>)
  })
}

function getRecordPhotoBindings(
  item: SharedDeviceOutboxEntry,
): Array<{ fieldKey: string; blobKey: string; fileId?: string }> {
  if (item.kind !== 'REGISTER_RECORD') return []
  if (Array.isArray(item.photoBindings) && item.photoBindings.length > 0) {
    return item.photoBindings
  }
  const payload = item.payload as Record<string, unknown> | undefined
  if (Array.isArray(payload?.photoBindings) && payload.photoBindings.length > 0) {
    return payload.photoBindings as Array<{ fieldKey: string; blobKey: string; fileId?: string }>
  }
  return []
}

function extractErrorCode(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const candidate = err as { code?: string; message?: string }
    if (typeof candidate.code === 'string' && candidate.code.trim().length > 0) {
      return candidate.code
    }
    if (typeof candidate.message === 'string' && candidate.message.trim().length > 0) {
      return candidate.message
    }
  }
  return fallback
}

/**
 * Flushes pending items in batches of SHARED_DEVICE_MAX_QUEUED_ITEMS (50) to the server.
 * - Only one flush runs at a time per device (concurrent calls no-op).
 * - For REGISTER_RECORD items with photo bindings, performs draft -> upload -> photos -> delete blobs.
 * - On success: deletes ok items, marks refused items REFUSED with code.
 * - On network error: resets batch status from SYNCING back to PENDING.
 * - On revoked device error: stops flush and leaves entries intact.
 */
export async function flushSharedDeviceOutbox(creds: {
  deviceId: string
  deviceSecret: string
}): Promise<{ processedCount: number; revokedStopped?: boolean }> {
  const { deviceId, deviceSecret } = creds

  if (activeFlushes.has(deviceId)) {
    return { processedCount: 0 }
  }

  activeFlushes.add(deviceId)
  let totalProcessed = 0

  try {
    // F2: Reset any stranded SYNCING entries from an earlier crash/reload back to PENDING.
    // Safe because activeFlushes mutex prevents concurrent runs per device and server opId replays.
    const initialEntries = (await runOutboxTx(deviceId, 'readonly', (store) => {
      return store.getAll()
    })) as SharedDeviceOutboxEntry[]

    const stranded = initialEntries.filter((e) => e.status === 'SYNCING')
    for (const item of stranded) {
      await runOutboxTx(deviceId, 'readwrite', (store) => {
        return store.put({ ...item, status: 'PENDING' } as unknown as Record<string, unknown>)
      })
    }

    while (true) {
      // 1. Fetch all items and filter pending, ordered by createdAt asc
      const allEntries = (await runOutboxTx(deviceId, 'readonly', (store) => {
        return store.getAll()
      })) as SharedDeviceOutboxEntry[]

      const pending = allEntries
        .filter((e) => e.status === 'PENDING')
        .sort((a, b) => a.createdAt - b.createdAt)

      if (pending.length === 0) {
        break
      }

      const batch = pending.slice(0, SHARED_DEVICE_MAX_QUEUED_ITEMS)

      // 2. Mark batch as SYNCING
      for (const item of batch) {
        await runOutboxTx(deviceId, 'readwrite', (store) => {
          return store.put({ ...item, status: 'SYNCING' } as unknown as Record<string, unknown>)
        })
      }

      const recordPhotoItems = batch.filter((item) => getRecordPhotoBindings(item).length > 0)
      const normalItems = batch.filter((item) => getRecordPhotoBindings(item).length === 0)

      // 3a. Process REGISTER_RECORD items with photo bindings
      for (const item of recordPhotoItems) {
        try {
          const bindings = getRecordPhotoBindings(item)

          // Step 1: Submit draft step if not already drafted
          let recordId = item.recordId
          if (!recordId) {
            const draftRes = await submitSharedDeviceQueued({
              deviceId,
              deviceSecret,
              items: [
                {
                  personUid: item.personUid,
                  offlineSessionId: item.offlineSessionId,
                  at: item.at,
                  kind: item.kind,
                  payload: item.payload,
                  opId: item.opId,
                },
              ],
            })
            const draftResult = draftRes.results?.find((r) => r.opId === item.opId)
            if (!draftResult || !draftResult.ok) {
              await runOutboxTx(deviceId, 'readwrite', (store) => {
                return store.put({
                  ...item,
                  status: 'REFUSED',
                  code: draftResult?.code ?? 'draft-failed',
                } as unknown as Record<string, unknown>)
              })
              continue
            }
            const draftPayload =
              draftResult.result && typeof draftResult.result === 'object'
                ? (draftResult.result as { recordId?: string })
                : undefined
            recordId = draftPayload?.recordId
            if (!recordId) {
              await runOutboxTx(deviceId, 'readwrite', (store) => {
                return store.put({
                  ...item,
                  status: 'REFUSED',
                  code: 'missing-draft-record-id',
                } as unknown as Record<string, unknown>)
              })
              continue
            }
            item.recordId = recordId
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.put(item as unknown as Record<string, unknown>)
            })
          }

          // Step 2: Upload each blob
          let uploadError: string | null = null
          for (const binding of bindings) {
            if (binding.fileId) continue
            const blobEntry = await getSharedDeviceBlob(deviceId, binding.blobKey)
            if (!blobEntry) {
              uploadError = 'blob-not-found'
              break
            }
            const buffer = await blobEntry.blob.arrayBuffer()
            const digest = await crypto.subtle.digest('SHA-256', buffer)
            const checksumSha256 = Array.from(new Uint8Array(digest))
              .map((b) => b.toString(16).padStart(2, '0'))
              .join('')

            const ext = blobEntry.contentType.includes('png') ? 'png' : 'jpg'
            const fileName =
              (item.payload?.fileName as string) ||
              `${item.opId}_${binding.fieldKey}.${ext}`

            let issueRes: { reservationId: string; uploadUrl: string }
            try {
              issueRes = await issueSharedDeviceUploadUrl({
                deviceId,
                deviceSecret,
                personUid: item.personUid,
                offlineSessionId: item.offlineSessionId,
                opId: `${item.opId}_${binding.fieldKey}`,
                artifactType: 'OPS_RECORD_PHOTO',
                artifactId: recordId,
                fileName,
                contentType: blobEntry.contentType,
                sizeBytes: blobEntry.size,
                checksumSha256,
              })
            } catch (err) {
              if (mapSharedDeviceError(err).state === 'revoked') throw err
              uploadError = extractErrorCode(err, 'issue-upload-failed')
              break
            }

            try {
              const putRes = await fetch(issueRes.uploadUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': blobEntry.contentType,
                },
                body: blobEntry.blob,
              })
              if (!putRes.ok) {
                uploadError = 'upload-failed'
                break
              }
            } catch (err) {
              if (mapSharedDeviceError(err).state === 'revoked') throw err
              uploadError = extractErrorCode(err, 'upload-failed')
              break
            }

            try {
              const finalizeRes = await finalizeSharedDeviceUpload({
                deviceId,
                deviceSecret,
                reservationId: issueRes.reservationId,
              })
              binding.fileId = finalizeRes.fileId
              item.photoBindings = bindings
              await runOutboxTx(deviceId, 'readwrite', (store) => {
                return store.put(item as unknown as Record<string, unknown>)
              })
            } catch (err) {
              if (mapSharedDeviceError(err).state === 'revoked') throw err
              uploadError = extractErrorCode(err, 'finalize-upload-failed')
              break
            }
          }

          if (uploadError) {
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.put({
                ...item,
                status: 'REFUSED',
                code: uploadError,
              } as unknown as Record<string, unknown>)
            })
            continue
          }

          // Step 3: Queue or submit REGISTER_RECORD_PHOTOS
          const photosRes = await submitSharedDeviceQueued({
            deviceId,
            deviceSecret,
            items: [
              {
                personUid: item.personUid,
                offlineSessionId: item.offlineSessionId,
                at: Date.now(),
                kind: 'REGISTER_RECORD_PHOTOS',
                opId: `${item.opId}_photos`,
                payload: {
                  recordId,
                  bindings: bindings.map((b) => ({
                    fieldKey: b.fieldKey,
                    fileId: b.fileId!,
                  })),
                },
              },
            ],
          })
          const photoResult =
            photosRes.results?.find((r) => r.opId === `${item.opId}_photos`) || photosRes.results?.[0]
          if (photoResult && photoResult.ok) {
            // Step 4: Delete blobs only after REGISTER_RECORD_PHOTOS succeeds
            for (const b of bindings) {
              if (b.blobKey) {
                await deleteSharedDeviceBlob(deviceId, b.blobKey)
              }
            }
            if (item.blobKey) {
              await deleteSharedDeviceBlob(deviceId, item.blobKey)
            }
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.delete(item.opId)
            })
          } else {
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.put({
                ...item,
                status: 'REFUSED',
                code: photoResult?.code ?? 'photos-refused',
              } as unknown as Record<string, unknown>)
            })
          }
        } catch (err) {
          if (mapSharedDeviceError(err).state === 'revoked') {
            for (const it of batch) {
              await runOutboxTx(deviceId, 'readwrite', (store) => {
                return store.put({ ...it, status: 'PENDING' } as unknown as Record<string, unknown>)
              })
            }
            return { processedCount: totalProcessed, revokedStopped: true }
          }
          await runOutboxTx(deviceId, 'readwrite', (store) => {
            return store.put({
              ...item,
              status: 'REFUSED',
              code: extractErrorCode(err, 'REFUSED'),
            } as unknown as Record<string, unknown>)
          })
        }
      }

      // 3b. Process normal items (checklist, attendance, record without photos)
      if (normalItems.length > 0) {
        let submitResult: SubmitSharedDeviceQueuedResult
        try {
          // Step C2: Upload check for proof and voice entries without fileId
          for (const item of normalItems) {
            if (
              (item.kind === 'CHECKLIST_PROOF' || item.kind === 'CHECKLIST_VOICE_MEMO') &&
              !item.fileId &&
              !item.payload?.fileId
            ) {
              if (!item.blobKey) {
                throw new Error(`Missing blobKey for queued ${item.kind} item ${item.opId}`)
              }
              const blobEntry = await getSharedDeviceBlob(deviceId, item.blobKey)
              if (!blobEntry) {
                throw new Error(`Blob not found for key ${item.blobKey}`)
              }
              const buffer = await blobEntry.blob.arrayBuffer()
              const digest = await crypto.subtle.digest('SHA-256', buffer)
              const checksumSha256 = Array.from(new Uint8Array(digest))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')

              const artifactType =
                item.kind === 'CHECKLIST_PROOF'
                  ? ('HELPER_CHECKLIST_PROOF' as const)
                  : ('HELPER_CHECKLIST_VOICE_MEMO' as const)
              const itemId = (item.payload.checklistItemId as string) || ''
              const defaultExt = item.kind === 'CHECKLIST_PROOF' ? 'jpg' : 'webm'
              const fileName =
                (item.payload.fileName as string) ||
                `${item.kind.toLowerCase()}_${item.opId}.${defaultExt}`

              const issueRes = await issueSharedDeviceUploadUrl({
                deviceId,
                deviceSecret,
                personUid: item.personUid,
                offlineSessionId: item.offlineSessionId,
                opId: item.opId,
                artifactType,
                artifactId: itemId,
                fileName,
                contentType: blobEntry.contentType,
                sizeBytes: blobEntry.size,
                checksumSha256,
              })

              const putRes = await fetch(issueRes.uploadUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': blobEntry.contentType,
                },
                body: blobEntry.blob,
              })
              if (!putRes.ok) {
                throw new Error(`Upload PUT failed with status ${putRes.status}`)
              }

              const finalizeRes = await finalizeSharedDeviceUpload({
                deviceId,
                deviceSecret,
                reservationId: issueRes.reservationId,
              })

              item.fileId = finalizeRes.fileId
              item.payload = {
                ...item.payload,
                fileId: finalizeRes.fileId,
              }

              await runOutboxTx(deviceId, 'readwrite', (store) => {
                return store.put(item as unknown as Record<string, unknown>)
              })
            } else if (item.kind === 'MARK_REQUEST_DONE') {
              const rawBlobKeys: string[] = (Array.isArray(item.blobKeys) && item.blobKeys.length > 0)
                ? item.blobKeys
                : (Array.isArray(item.payload?.blobKeys) && (item.payload.blobKeys as string[]).length > 0)
                ? (item.payload.blobKeys as string[])
                : item.blobKey
                ? [item.blobKey]
                : (item.payload?.blobKey ? [item.payload.blobKey as string] : [])

              const existingFileIds: string[] = Array.isArray(item.fileIds)
                ? [...item.fileIds]
                : Array.isArray(item.payload?.fileIds)
                ? [...(item.payload.fileIds as string[])]
                : []

              for (let i = existingFileIds.length; i < rawBlobKeys.length; i++) {
                const bKey = rawBlobKeys[i]
                const blobEntry = await getSharedDeviceBlob(deviceId, bKey)
                if (!blobEntry) {
                  throw new Error(`Blob not found for key ${bKey}`)
                }
                const buffer = await blobEntry.blob.arrayBuffer()
                const digest = await crypto.subtle.digest('SHA-256', buffer)
                const checksumSha256 = Array.from(new Uint8Array(digest))
                  .map((b) => b.toString(16).padStart(2, '0'))
                  .join('')

                const reqId = (item.payload?.requestId as string) || ''
                const fileName = `request_photo_${item.opId}_${i}.jpg`

                const issueRes = await issueSharedDeviceUploadUrl({
                  deviceId,
                  deviceSecret,
                  personUid: item.personUid,
                  offlineSessionId: item.offlineSessionId,
                  opId: `${item.opId}_photo_${i}`,
                  artifactType: 'OPS_REQUEST_PHOTO',
                  artifactId: reqId,
                  fileName,
                  contentType: blobEntry.contentType || 'image/jpeg',
                  sizeBytes: blobEntry.size,
                  checksumSha256,
                })

                const putRes = await fetch(issueRes.uploadUrl, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': blobEntry.contentType || 'image/jpeg',
                  },
                  body: blobEntry.blob,
                })
                if (!putRes.ok) {
                  throw new Error(`Upload PUT failed with status ${putRes.status}`)
                }

                const finalizeRes = await finalizeSharedDeviceUpload({
                  deviceId,
                  deviceSecret,
                  reservationId: issueRes.reservationId,
                })

                existingFileIds.push(finalizeRes.fileId)
                item.fileIds = existingFileIds
                item.payload = {
                  ...item.payload,
                  fileIds: existingFileIds,
                }

                await runOutboxTx(deviceId, 'readwrite', (store) => {
                  return store.put(item as unknown as Record<string, unknown>)
                })
              }
            } else if (item.kind === 'CON_ISSUE_ATTACH_PHOTOS') {
              const rawBlobKeys: string[] = (Array.isArray(item.blobKeys) && item.blobKeys.length > 0)
                ? item.blobKeys
                : (Array.isArray(item.payload?.blobKeys) && (item.payload.blobKeys as string[]).length > 0)
                ? (item.payload.blobKeys as string[])
                : item.blobKey
                ? [item.blobKey]
                : (item.payload?.blobKey ? [item.payload.blobKey as string] : [])

              const existingFileIds: string[] = Array.isArray(item.fileIds)
                ? [...item.fileIds]
                : Array.isArray(item.payload?.fileIds)
                ? [...(item.payload.fileIds as string[])]
                : []

              for (let i = existingFileIds.length; i < rawBlobKeys.length; i++) {
                const bKey = rawBlobKeys[i]
                const blobEntry = await getSharedDeviceBlob(deviceId, bKey)
                if (!blobEntry) {
                  throw new Error(`Blob not found for key ${bKey}`)
                }
                const buffer = await blobEntry.blob.arrayBuffer()
                const digest = await crypto.subtle.digest('SHA-256', buffer)
                const checksumSha256 = Array.from(new Uint8Array(digest))
                  .map((b) => b.toString(16).padStart(2, '0'))
                  .join('')

                const issueId = (item.payload?.issueId as string) || ''
                const fileName = `con_issue_${item.opId}_${i}.jpg`

                const issueRes = await issueSharedDeviceUploadUrl({
                  deviceId,
                  deviceSecret,
                  personUid: item.personUid,
                  offlineSessionId: item.offlineSessionId,
                  opId: `${item.opId}_photo_${i}`,
                  artifactType: 'CON_ISSUE_PHOTO',
                  artifactId: issueId,
                  fileName,
                  contentType: blobEntry.contentType || 'image/jpeg',
                  sizeBytes: blobEntry.size,
                  checksumSha256,
                })

                const putRes = await fetch(issueRes.uploadUrl, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': blobEntry.contentType || 'image/jpeg',
                  },
                  body: blobEntry.blob,
                })
                if (!putRes.ok) {
                  throw new Error(`Upload PUT failed with status ${putRes.status}`)
                }

                const finalizeRes = await finalizeSharedDeviceUpload({
                  deviceId,
                  deviceSecret,
                  reservationId: issueRes.reservationId,
                })

                existingFileIds.push(finalizeRes.fileId)
                item.fileIds = existingFileIds
                item.payload = {
                  ...item.payload,
                  fileIds: existingFileIds,
                }

                await runOutboxTx(deviceId, 'readwrite', (store) => {
                  return store.put(item as unknown as Record<string, unknown>)
                })
              }
            } else if (item.kind === 'DPR_SAVE') {
              const payload = (item.payload || {}) as Record<string, unknown>
              const projectId = String(payload.projectId || '')
              const reportDate = String(payload.reportDate || '')
              const shift = String(payload.shift || 'DAY')
              const dprId = `dpr_${projectId}_${reportDate}_${shift}`

              const rawLines = Array.isArray(payload.lines) ? [...payload.lines] : []
              const lineBlobs = Array.isArray(payload.lineBlobs)
                ? (payload.lineBlobs as Array<{ lineId: string; blobKey?: string; blobKeys?: string[] }>)
                : []

              let changed = false
              for (const lb of lineBlobs) {
                const bKeys = Array.isArray(lb.blobKeys) ? lb.blobKeys : lb.blobKey ? [lb.blobKey] : []
                for (let i = 0; i < bKeys.length; i++) {
                  const bKey = bKeys[i]
                  const blobEntry = await getSharedDeviceBlob(deviceId, bKey)
                  if (!blobEntry) continue

                  const buffer = await blobEntry.blob.arrayBuffer()
                  const digest = await crypto.subtle.digest('SHA-256', buffer)
                  const checksumSha256 = Array.from(new Uint8Array(digest))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('')

                  const fileName = `dpr_photo_${item.opId}_${lb.lineId}_${i}.jpg`
                  const issueRes = await issueSharedDeviceUploadUrl({
                    deviceId,
                    deviceSecret,
                    personUid: item.personUid,
                    offlineSessionId: item.offlineSessionId,
                    opId: `${item.opId}_${lb.lineId}_${i}`,
                    artifactType: 'OPS_RECORD_PHOTO',
                    artifactId: dprId,
                    fileName,
                    contentType: blobEntry.contentType || 'image/jpeg',
                    sizeBytes: blobEntry.size,
                    checksumSha256,
                  })

                  const putRes = await fetch(issueRes.uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': blobEntry.contentType || 'image/jpeg' },
                    body: blobEntry.blob,
                  })
                  if (!putRes.ok) {
                    throw new Error(`Upload PUT failed with status ${putRes.status}`)
                  }

                  const finalizeRes = await finalizeSharedDeviceUpload({
                    deviceId,
                    deviceSecret,
                    reservationId: issueRes.reservationId,
                  })

                  const line = rawLines.find(
                    (l: unknown) =>
                      typeof l === 'object' && l !== null && (l as { lineId?: unknown }).lineId === lb.lineId,
                  ) as Record<string, unknown> | undefined
                  if (line) {
                    line.fileIds = Array.isArray(line.fileIds)
                      ? [...(line.fileIds as string[]), finalizeRes.fileId]
                      : [finalizeRes.fileId]
                    changed = true
                  }
                }
              }

              if (changed) {
                item.payload = {
                  ...item.payload,
                  lines: rawLines,
                }
                await runOutboxTx(deviceId, 'readwrite', (store) => {
                  return store.put(item as unknown as Record<string, unknown>)
                })
              }
            }
          }

          // 3. Prepare payload
          const queuedItems: SharedDeviceQueuedItem[] = normalItems.map((b) => ({
            personUid: b.personUid,
            offlineSessionId: b.offlineSessionId,
            at: b.at,
            kind: b.kind,
            payload: b.payload,
            opId: b.opId,
          }))

          submitResult = await submitSharedDeviceQueued({
            deviceId,
            deviceSecret,
            items: queuedItems,
          })
        } catch (err) {
          // F1 & F2: Put entries back to PENDING on any error (including revoke)
          for (const item of normalItems) {
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.put({ ...item, status: 'PENDING' } as unknown as Record<string, unknown>)
            })
          }

          if (mapSharedDeviceError(err).state === 'revoked') {
            // On revoked device error, stop and report revokedStopped
            return { processedCount: totalProcessed, revokedStopped: true }
          }

          throw err
        }

        // 4. Process results
        const resultMap = new Map(submitResult.results.map((r) => [r.opId, r]))
        for (const item of normalItems) {
          const res = resultMap.get(item.opId)
          if (res && res.ok) {
            if (item.kind === 'CON_ISSUE_CREATE') {
              const resResult = (res.result || {}) as Record<string, unknown>
              const issueObj = (resResult.issue || {}) as Record<string, unknown>
              const createdIssueId =
                typeof resResult.issueId === 'string'
                  ? resResult.issueId
                  : typeof issueObj.id === 'string'
                  ? issueObj.id
                  : ''

              const rawBlobKeys: string[] =
                Array.isArray(item.blobKeys) && item.blobKeys.length > 0
                  ? item.blobKeys
                  : Array.isArray(item.payload?.blobKeys) && (item.payload.blobKeys as string[]).length > 0
                  ? (item.payload.blobKeys as string[])
                  : []

              if (createdIssueId && rawBlobKeys.length > 0) {
                const wsId = String((item.payload as Record<string, unknown> | undefined)?.workspaceId || '')
                const attachEntry: SharedDeviceOutboxEntry = {
                  opId: `${item.opId}_photos`,
                  personUid: item.personUid,
                  offlineSessionId: item.offlineSessionId,
                  at: Date.now(),
                  kind: 'CON_ISSUE_ATTACH_PHOTOS',
                  payload: {
                    workspaceId: wsId,
                    issueId: createdIssueId,
                    fileIds: [],
                  },
                  status: 'PENDING',
                  createdAt: Date.now(),
                  blobKeys: rawBlobKeys,
                }
                await runOutboxTx(deviceId, 'readwrite', (store) => {
                  store.put(attachEntry as unknown as Record<string, unknown>)
                  return store.delete(item.opId)
                })
                continue
              }
            }

            // Delete blob only after server applies item
            if (item.blobKey) {
              await deleteSharedDeviceBlob(deviceId, item.blobKey)
            }
            if (Array.isArray(item.blobKeys)) {
              for (const bk of item.blobKeys) {
                if (bk !== item.blobKey) {
                  await deleteSharedDeviceBlob(deviceId, bk)
                }
              }
            }
            if (Array.isArray(item.payload?.blobKeys)) {
              for (const bk of item.payload.blobKeys as string[]) {
                if (bk !== item.blobKey && !item.blobKeys?.includes(bk)) {
                  await deleteSharedDeviceBlob(deviceId, bk)
                }
              }
            }
            // Delete ok item
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.delete(item.opId)
            })
          } else {
            // Mark refused item REFUSED with code and keep (keeps blob)
            const code = res?.code ?? 'REFUSED'
            await runOutboxTx(deviceId, 'readwrite', (store) => {
              return store.put({
                ...item,
                status: 'REFUSED',
                code,
              } as unknown as Record<string, unknown>)
            })
          }
        }
      }

      totalProcessed += batch.length

      if (batch.length < SHARED_DEVICE_MAX_QUEUED_ITEMS) {
        break
      }
    }

    return { processedCount: totalProcessed }
  } finally {
    activeFlushes.delete(deviceId)
  }
}

/**
 * Lists all refused items stored in the device outbox.
 */
export async function listRefusedSharedDeviceItems(
  deviceId: string,
): Promise<SharedDeviceOutboxEntry[]> {
  const allEntries = (await runOutboxTx(deviceId, 'readonly', (store) => {
    return store.getAll()
  })) as SharedDeviceOutboxEntry[]

  return allEntries.filter((e) => e.status === 'REFUSED')
}

/**
 * Clears or deletes the shared device outbox database.
 */
export async function clearSharedDeviceOutbox(deviceId: string): Promise<void> {
  const cached = dbCache.get(deviceId)
  if (cached) {
    try {
      const db = await cached
      db.close()
    } catch {
      // Ignore close error
    }
    dbCache.delete(deviceId)
  }

  if (typeof indexedDB === 'undefined') return

  const dbName = `helper-outbox-shared-${deviceId}`
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error ?? new Error('Failed to delete outbox database'))
    req.onblocked = () => resolve()
  })
}
